import React, { useCallback, useState } from 'react';
import { useSelector } from 'react-redux';
import { AppHeader } from 'taboola-ultimate-ui';
import { HOME_PATH } from '../../../components';
import { LOGOUT_PATH } from '../../../components/AppHeader/AppHeader';
import Logo from '../../../components/AppHeader/components/Logo';
import UserMenu from '../../../components/UserMenu';
import { useNavigate } from '../../../hooks';
import { usePlatform } from '../../../hooks/usePlatform';
import { useAccount } from '../../taboola-common-frontend-modules/account-configurations';
import { userSelector } from '../../taboola-common-frontend-modules/authentication';
import { FormattedMessage } from '../../taboola-common-frontend-modules/i18n';
import { AccountSelectionStep } from '../components/steps/AccountSelectionStep';
import { IntroductionStep } from '../components/steps/IntroductionStep';
import { useThirdPartyConnect } from '../hooks/useThirdPartyConnect';
import { getPlatformName } from '../utils/3p-platforms';
import styles from './thirdPartyConnect.module.scss';

export const ThirdPartyConnectContent = () => {
    const account = useAccount();
    const [currentPage, setCurrentPage] = useState('intro');
    const [selectedAccount, setSelectedAccount] = useState(account);
    const user = useSelector(userSelector);
    const navigate = useNavigate(false);
    const handleLogoutButtonClick = useCallback(() => navigate(LOGOUT_PATH), [navigate]);

    const platformCode = usePlatform();
    const platformName = getPlatformName(platformCode);

    // Navigation functions
    const goToNextPage = () => setCurrentPage('select');
    const goToPrevPage = () => setCurrentPage('intro');

    const { handleSendAccountId } = useThirdPartyConnect(selectedAccount);

    const handleCancel = useCallback(() => {
        window.close();
    }, []);

    // Unified configuration for all steps
    const STEPS = {
        intro: {
            id: 'intro',
            label: <FormattedMessage id="3p-connect.step.intro" defaultMessage="Introduction" />,
            component: (
                <IntroductionStep
                    platformCode={platformCode}
                    platformName={platformName}
                    onNext={goToNextPage}
                    onCancel={handleCancel}
                />
            ),
        },
        select: {
            id: 'select',
            label: <FormattedMessage id="3p-connect.step.select" defaultMessage="Select Account" />,
            component: (
                <AccountSelectionStep
                    platformCode={platformCode}
                    onBack={goToPrevPage}
                    onConnect={handleSendAccountId}
                    onAccountSelect={setSelectedAccount}
                    selectedAccount={selectedAccount}
                />
            ),
        },
    };

    // Get the ordered list of steps for navigation
    const stepsOrder = Object.values(STEPS);

    return (
        <div className={styles.wrapper}>
            <AppHeader
                brandingWidth={0}
                logoComponent={<Logo className={styles['logo']} path={HOME_PATH} aria-label="Realize Logo" />}
                height={parseInt(styles.headerHeight)}
                zIndex={2}
                backgroundColor={styles.headerBackgroundColor}
                className={styles['header-container']}
                contentClassName={styles['header-content']}
            >
                <UserMenu
                    className={styles['user-name']}
                    userName={user['full_name']}
                    email={user.username}
                    buttonText={<FormattedMessage id="app.actionButtons.signOut" defaultMessage="Sign Out" />}
                    onButtonClick={handleLogoutButtonClick}
                    dropdownIconClassName={styles['menu-dropdown-icon']}
                />
            </AppHeader>
            <div className={styles.container}>
                <div className={styles['wizard-navigation']}>
                    {stepsOrder.map(step => (
                        <div
                            key={step.id}
                            className={`${styles['nav-item']} ${currentPage === step.id ? styles['active'] : ''}`}
                            onClick={() => setCurrentPage(step.id)}
                        >
                            {step.label}
                        </div>
                    ))}
                </div>
                <div className={styles['wizard-content']}>{STEPS[currentPage].component}</div>
            </div>
        </div>
    );
};
